import { Button } from '@mantine/core';
import { IconDeviceMobile, IconDotsVertical, IconInfoCircle } from '@tabler/icons-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import logo from 'src/assets/logo.svg';
import { LogoHeader } from 'src/Components/Shared/LogoHeader';
import { useInstallPwa } from 'src/Hooks/Shared/useInstallPwa';

import styles from './InstallPage.module.scss';

type Platform = 'ios-safari' | 'ios-other' | 'android' | 'desktop' | 'standalone';

const IosShareIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 50 50"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M25 33 V8 M16 17 L25 8 L34 17" />
    <path d="M14 22 H10 a3 3 0 0 0 -3 3 V42 a3 3 0 0 0 3 3 H40 a3 3 0 0 0 3 -3 V25 a3 3 0 0 0 -3 -3 H36" />
  </svg>
);

const detectPlatform = (): Platform => {
  if (typeof window === 'undefined') return 'desktop';

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;

  if (isStandalone) return 'standalone';

  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/i.test(ua)) {
    const isInAppBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line/i.test(ua);
    return isInAppBrowser ? 'ios-other' : 'ios-safari';
  }

  if (/Android/i.test(ua)) return 'android';

  return 'desktop';
};

const IosSafariView = () => (
  <>
    <div className={styles.title}>Installez Share Heritage sur votre iPhone</div>
    <div className={styles.subtitle}>3 étapes, 30 secondes, c'est promis.</div>
    <div className={styles.steps}>
      <div className={styles.step}>
        <span className={styles.stepNumber}>1</span>
        <div className={styles.stepContent}>
          Appuyez sur l'icône Partager
          <span className={styles.inlineIcon}>
            <IosShareIcon size={18} />
          </span>
          en bas de votre écran Safari.
        </div>
      </div>
      <div className={styles.step}>
        <span className={styles.stepNumber}>2</span>
        <div className={styles.stepContent}>
          Faites défiler et choisissez « Sur l'écran d'accueil ».
        </div>
      </div>
      <div className={styles.step}>
        <span className={styles.stepNumber}>3</span>
        <div className={styles.stepContent}>
          Appuyez sur « Ajouter » en haut à droite. C'est tout&nbsp;!
        </div>
      </div>
    </div>
  </>
);

const IosOtherView = () => (
  <>
    <div className={styles.title}>Ouvrez cette page dans Safari</div>
    <div className={styles.subtitle}>
      L'installation sur iPhone ne fonctionne qu'avec Safari, pas dans ce navigateur.
    </div>
    <div className={styles.warning}>
      <IconInfoCircle size={24} />
      <span>
        Copiez l'adresse de cette page, ouvrez Safari, puis collez-la pour continuer l'installation.
      </span>
    </div>
  </>
);

const AndroidView = () => {
  const { supportsPWA, installPwa } = useInstallPwa();

  return (
    <>
      <div className={styles.title}>Installez Share Heritage sur votre Android</div>
      <div className={styles.subtitle}>Un seul tap, et l'app sera sur votre écran d'accueil.</div>
      {supportsPWA ? (
        <Button size="lg" className={styles.installButton} onClick={installPwa}>
          Installer l'application
        </Button>
      ) : (
        <div className={styles.steps}>
          <div className={styles.step}>
            <span className={styles.stepNumber}>1</span>
            <div className={styles.stepContent}>
              Appuyez sur le menu
              <IconDotsVertical size={18} className={styles.inlineIcon} />
              en haut à droite de Chrome.
            </div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNumber}>2</span>
            <div className={styles.stepContent}>Choisissez « Installer l'application ».</div>
          </div>
          <div className={styles.step}>
            <span className={styles.stepNumber}>3</span>
            <div className={styles.stepContent}>
              Confirmez. Share Heritage apparaît sur votre écran d'accueil.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DesktopView = ({ url }: { url: string }) => (
  <>
    <div className={styles.title}>Installez Share Heritage sur votre téléphone</div>
    <div className={styles.subtitle}>
      <IconDeviceMobile size={20} className={styles.inlineIcon} />
      Scannez ce QR code avec votre téléphone pour ouvrir cette page.
    </div>
    <div className={styles.qrCode}>
      <QRCodeSVG
        value={url}
        size={240}
        imageSettings={{ src: logo, height: 48, width: 48, excavate: true }}
        level="H"
        fgColor="#114B50"
      />
    </div>
  </>
);

const InstallPage = () => {
  const [platform, setPlatform] = useState<Platform>('desktop');

  useEffect(() => {
    const update = () => setPlatform(detectPlatform());
    update();

    const standaloneMedia = window.matchMedia('(display-mode: standalone)');
    standaloneMedia.addEventListener('change', update);
    window.addEventListener('appinstalled', update);
    window.addEventListener('focus', update);

    return () => {
      standaloneMedia.removeEventListener('change', update);
      window.removeEventListener('appinstalled', update);
      window.removeEventListener('focus', update);
    };
  }, []);

  if (platform === 'standalone') {
    return <Navigate to="/" replace />;
  }

  const url = typeof window !== 'undefined' ? `${window.location.origin}/install` : '';

  return (
    <div className={styles.container}>
      <div className={styles.subcontainer}>
        <LogoHeader />
        {platform === 'ios-safari' && <IosSafariView />}
        {platform === 'ios-other' && <IosOtherView />}
        {platform === 'android' && <AndroidView />}
        {platform === 'desktop' && <DesktopView url={url} />}
      </div>
    </div>
  );
};

export default InstallPage;
